import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, fromJS } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import _get from 'lodash/get'
import { useQuery } from '@apollo/client'
import { GET_CONTENT } from '../../services/content/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import {
  CONTENT_IMAGE_LIST,
  formatDataToLegacy,
} from '../../services/content/index'
import { EN } from '../../services/languages/constants'
import Spotlight from '../Spotlight/index'
import Promotion from '../Promotion/index'
import PromotionGaiaOracle from '../PromotionGaiaOracle/index'

export const SECTION_TYPES = {
  SPOTLIGHT: 'spotlight',
  PROMOTION: 'promotion',
  GAIA_ORACLE: 'ai-chat-promotion',
}

function SectionGraphQLManager (props) {
  const {
    titleGroupIds,
    language,
    fetchPolicy,
    authJwt,
    history,
    sectionId,
    sectionIndex,
    rowPlacement,
    onVisibilityChange,
    ga4ImpressionEventData,
    sectionType,
    prevSectionType,
  } = props

  // query GraphQL for the data
  const { data, loading } = useQuery(GET_CONTENT, {
    variables: {
      language: [language, '_primary'],
      primaryLanguage: [language],
      titleGroupIds,
      imageType: CONTENT_IMAGE_LIST,
      limitedData: true,
      includeSeriesImage: false,
      learnerLevelLanguage: language,
    },
    fetchPolicy,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const contentData = _get(data, ['contentList', 'content', 0], []) || []
  const formattedContentData = fromJS(formatDataToLegacy(contentData))

  if (sectionType === SECTION_TYPES.SPOTLIGHT) {
    return (
      <Spotlight
        sectionId={sectionId}
        history={history}
        sectionIndex={sectionIndex}
        onVisibilityChange={onVisibilityChange}
        ga4ImpressionEventData={ga4ImpressionEventData || Map()}
        graphQLData={formattedContentData}
      />
    )
  }

  if (sectionType === SECTION_TYPES.PROMOTION) {
    return (
      <Promotion
        sectionId={sectionId}
        sectionIndex={sectionIndex}
        rowPlacement={rowPlacement}
        graphQLData={formattedContentData}
        graphQLProcessing={loading}
      />
    )
  }

  if (sectionType === SECTION_TYPES.GAIA_ORACLE) {
    return (
      <PromotionGaiaOracle
        sectionId={sectionId}
        sectionIndex={sectionIndex}
        rowPlacement={rowPlacement}
        data={formattedContentData}
        loading={loading}
        history={history}
        prevSectionType={prevSectionType}
      />
    )
  }

  return null
}

SectionGraphQLManager.propTypes = {
  titleGroupIds: PropTypes.array.isRequired,
  fetchPolicy: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  upstreamContext: ImmutablePropTypes.map.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  rowPlacement: PropTypes.number.isRequired,
  sectionId: PropTypes.string,
  history: PropTypes.object,
  onVisibilityChange: PropTypes.func,
  ga4ImpressionEventData: ImmutablePropTypes.map,
  sectionType: PropTypes.oneOf([
    SECTION_TYPES.PROMOTION,
    SECTION_TYPES.SPOTLIGHT,
    SECTION_TYPES.GAIA_ORACLE,
  ]).isRequired,
  prevSectionType: PropTypes.string,
}

SectionGraphQLManager.defaultProps = {
  titleGroupIds: [],
  fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  upstreamContext: Map(),
  sectionIndex: 1,
  rowPlacement: 1,
  language: EN,
}

export default compose(
  connectRedux(
    (state) => {
      return {
        authJwt: state.auth.get('jwt'),
      }
    },
  ),
)(SectionGraphQLManager)
